/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../common-components/PageHeader';
import EmptyState from '../../common-components/EmptyState';
import ConfirmDialog from '../../common-components/ConfirmDialog';
import { useAppDispatch, useAppSelector } from '../../helpers/hooks';
import { useProjectsSlice } from './slice';
import { selectProjects, selectProjectsLoading } from './slice/selectors';
import { Project } from '../../helpers/types';
import { ROUTE_PATHS } from '../../routes/constants';
import CreateProjectDialog from './components/CreateProjectDialog';

export default function ProjectsPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { actions } = useProjectsSlice();
  const projects = useAppSelector(selectProjects);
  const loading = useAppSelector(selectProjectsLoading);

  const [createOpen, setCreateOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Project | null>(null);

  useEffect(() => {
    dispatch(actions.fetchRequest());
  }, []);

  const confirmDelete = () => {
    if (toDelete) {
      dispatch(actions.deleteRequest(toDelete.id));
      setToDelete(null);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Projects"
        subtitle="Manage translation projects and their languages."
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            New project
          </Button>
        }
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : projects.length === 0 ? (
        <EmptyState
          icon={<FolderOpenIcon fontSize="inherit" />}
          title="No projects yet"
          description="Create your first project to start adding keys and translating them with AI."
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
            >
              New project
            </Button>
          }
        />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              lg: '1fr 1fr 1fr',
            },
          }}
        >
          {projects.map((project) => (
            <Card key={project.id} sx={{ position: 'relative' }}>
              <CardActionArea
                onClick={() => navigate(ROUTE_PATHS.PROJECT(project.id))}
              >
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Typography variant="h6" sx={{ pr: 4 }}>
                      {project.name}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, minHeight: 40 }}
                    color="text.secondary"
                  >
                    {project.description || 'No description'}
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 2, flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip
                      size="small"
                      color="primary"
                      label={`Source: ${project.sourceLanguage}`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${project.targetLanguages.length} target languages`}
                    />
                  </Stack>
                </CardContent>
              </CardActionArea>
              <Tooltip title="Delete project">
                <IconButton
                  size="small"
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                  onClick={() => setToDelete(project)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Card>
          ))}
        </Box>
      )}

      <CreateProjectDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <ConfirmDialog
        open={Boolean(toDelete)}
        title="Delete project"
        message={`Delete "${toDelete?.name}"? This removes all its keys and translations.`}
        confirmLabel="Delete"
        destructive
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </Box>
  );
}
